/**
 * StateViewportManager - Manages multiple chart states via innerHTML caching
 *
 * MVP approach: saves/restores innerHTML of viewport-content for each state.
 * This avoids container ID collisions while providing instant state switching.
 *
 * Features:
 * - Lazy caching: states saved only after first render
 * - LRU eviction: removes oldest states when limit exceeded
 * - Instant switching: innerHTML restore (no re-render)
 * - Planet drag integration: proper state ID tracking
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import { MAX_RENDERED_STATES } from '../../../../../config/stateConfig';

/**
 * Cached state info
 */
export interface CachedStateInfo {
  stateId: string;
  innerHTML: string;
  lastAccessed: Date;
}

/**
 * Manages chart states via innerHTML caching
 */
export class StateViewportManager {
  private chartId: string;
  private cachedStates: Map<string, CachedStateInfo> = new Map();
  private activeStateId: string | null = null;
  private maxStates: number;

  constructor(chartId: string, maxStates: number = MAX_RENDERED_STATES) {
    this.chartId = chartId;
    this.maxStates = maxStates;
  }

  /**
   * Get viewport-content element
   */
  private getViewportContent(): Element | null {
    return document.querySelector(`.viewport-content-${this.chartId}`);
  }

  /**
   * Check if a state is cached
   */
  isStateRendered(stateId: string): boolean {
    return this.cachedStates.has(stateId);
  }

  /**
   * Switch to a cached state (restore innerHTML)
   * Returns true if successful, false if state not cached
   */
  switchToState(stateId: string): boolean {
    const cached = this.cachedStates.get(stateId);
    if (!cached) {
      devLog(`[StateViewportManager] State ${stateId} not cached`);
      return false;
    }

    const viewport = this.getViewportContent();
    if (!viewport) {
      devWarn(`[StateViewportManager] viewport-content-${this.chartId} not found`);
      return false;
    }

    // Save current state if it has an active ID
    if (this.activeStateId && this.activeStateId !== stateId) {
      this.saveCurrentState();
    }

    // Restore cached state
    viewport.innerHTML = cached.innerHTML;
    cached.lastAccessed = new Date();
    this.activeStateId = stateId;

    devLog(`[StateViewportManager] ⚡ Switched to state ${stateId}`);
    return true;
  }

  /**
   * Save current viewport content as a state
   */
  saveState(stateId: string): void {
    const viewport = this.getViewportContent();
    if (!viewport) {
      devWarn(`[StateViewportManager] Cannot save state - viewport not found`);
      return;
    }

    // Evict old states if at limit
    this.evictOldStates();

    this.cachedStates.set(stateId, {
      stateId,
      innerHTML: viewport.innerHTML,
      lastAccessed: new Date()
    });

    this.activeStateId = stateId;
    devLog(`[StateViewportManager] Saved state ${stateId}, total: ${this.cachedStates.size}`);
  }

  /**
   * Save current state (using activeStateId)
   */
  private saveCurrentState(): void {
    if (!this.activeStateId) return;

    const viewport = this.getViewportContent();
    if (!viewport) return;

    const existing = this.cachedStates.get(this.activeStateId);
    if (existing) {
      existing.innerHTML = viewport.innerHTML;
      existing.lastAccessed = new Date();
    }
  }

  /**
   * Get the currently active state ID
   */
  getActiveStateId(): string | null {
    return this.activeStateId;
  }

  /**
   * Set active state ID (after rendering)
   */
  setActiveStateId(stateId: string): void {
    this.activeStateId = stateId;
  }

  /**
   * Remove a specific state from cache
   */
  removeState(stateId: string): void {
    this.cachedStates.delete(stateId);
    if (this.activeStateId === stateId) {
      this.activeStateId = null;
    }
    devLog(`[StateViewportManager] Removed state ${stateId}`);
  }

  /**
   * Get all cached state IDs
   */
  getCachedStateIds(): string[] {
    return Array.from(this.cachedStates.keys());
  }

  /**
   * Get count of cached states
   */
  getCachedCount(): number {
    return this.cachedStates.size;
  }

  /**
   * Evict oldest states when limit exceeded (LRU)
   */
  private evictOldStates(): void {
    if (this.cachedStates.size < this.maxStates) return;

    // Get states sorted by last accessed (oldest first), excluding active
    const sortedStates = Array.from(this.cachedStates.values())
      .filter(s => s.stateId !== this.activeStateId)
      .sort((a, b) => a.lastAccessed.getTime() - b.lastAccessed.getTime());

    // Evict oldest until under limit
    const toEvict = sortedStates.slice(0, this.cachedStates.size - this.maxStates + 1);

    for (const state of toEvict) {
      this.removeState(state.stateId);
      devLog(`[StateViewportManager] Evicted old state ${state.stateId}`);
    }
  }

  /**
   * Clear all cached states
   */
  clearAll(): void {
    this.cachedStates.clear();
    this.activeStateId = null;
    devLog(`[StateViewportManager] Cleared all states for ${this.chartId}`);
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    this.clearAll();
    devLog(`[StateViewportManager] Destroyed for ${this.chartId}`);
  }

  /**
   * Get statistics for debugging
   */
  getStats(): {
    chartId: string;
    cachedCount: number;
    maxStates: number;
    activeStateId: string | null;
    states: Array<{ stateId: string; lastAccessed: Date; sizeKB: number }>;
  } {
    return {
      chartId: this.chartId,
      cachedCount: this.cachedStates.size,
      maxStates: this.maxStates,
      activeStateId: this.activeStateId,
      states: Array.from(this.cachedStates.values()).map(s => ({
        stateId: s.stateId,
        lastAccessed: s.lastAccessed,
        sizeKB: Math.round(s.innerHTML.length / 1024 * 10) / 10
      }))
    };
  }
}
